import type {
  GetUserDetailsRequestProto_Body,
  GetUserDetailsResponseProto,
} from "@noom/noom-contracts/noom_contracts/backend/user/user_service";
import { appConfig } from "src/config";
import { getAuthorizationHeader } from "src/api/utils";
import { captureException } from "@utils/error";

export type GetUserProfileResponse = {
  accessCode: string;
  name: string;
};

/**
 * Calls the CS getUserDetails endpoint to fetch user details
 * @param accessCode - The user's access code
 * @returns Promise with user details response, or null when coach server has
 * no record for the user.
 */
export async function getUserDetails(
  accessCode: string
): Promise<GetUserDetailsResponseProto | null> {
  const requestBody: GetUserDetailsRequestProto_Body = {
    accessCode,
  };

  const response = await fetch(
    `${appConfig.COACH_SERVER_URL}/users/getUserDetails`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthorizationHeader(),
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to get user details: ${response.status}`);
  }

  return response.json();
}

export async function getActiveProgram(accessCode: string): Promise<any> {
  try {
    const response = await fetch(
      `${appConfig.COACH_SERVER_URL}/program/v1/users/${accessCode}/programs:getActive`,
      {
        method: "GET",
        headers: {
          ...getAuthorizationHeader(),
        },
      }
    );
    if (!response.ok) {
      return null;
    }
    return response.json();
  } catch (error) {
    captureException(error, "api.userDetails.getActiveProgram");
    return null;
  }
}

/**
 * Calls the CS getUserProfile endpoint to fetch user profile data
 * @param accessCode - The user's access code
 * @returns Promise with user profile response or null if we fail to fetch the profile
 */
export async function getUserProfile(
  accessCode: string
): Promise<GetUserProfileResponse | null> {
  try {
    const response = await fetch(
      `${appConfig.COACH_SERVER_URL}/users/${accessCode}/profile`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthorizationHeader(),
        },
      }
    );

    if (!response.ok) {
      return null;
    }

    return response.json();
  } catch (error) {
    return null;
  }
}
